import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PageBackground from '../components/PageBackground';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import apiService from '../services/api';
import './BhaktiSamadhan.css';

const CATEGORIES = ['Education', 'Career', 'Marriage', 'Health', 'Wealth', 'Family', 'Protection', 'Children', 'Spiritual Growth', 'All Remedies'];
const CATEGORY_TRANSLATION_KEYS = {
  Education: 'bhaktiSamadhan.categories.education',
  Career: 'bhaktiSamadhan.categories.career',
  Marriage: 'bhaktiSamadhan.categories.marriage',
  Health: 'bhaktiSamadhan.categories.health',
  Wealth: 'bhaktiSamadhan.categories.wealth',
  Family: 'bhaktiSamadhan.categories.family',
  Protection: 'bhaktiSamadhan.categories.protection',
  Children: 'bhaktiSamadhan.categories.children',
  'Spiritual Growth': 'bhaktiSamadhan.categories.spiritualGrowth',
  'All Remedies': 'bhaktiSamadhan.categories.allRemedies',
};
const DEFAULT_GUEST_LIMIT_INFO = {
  isReached: false,
  message: '',
  quotaLimit: 3,
  quotaUsed: 0,
  quotaResetTime: ''
};
const LANGUAGE_LOCALE_MAP = {
  en: 'en-IN',
  hi: 'hi-IN',
  or: 'or-IN',
  bn: 'bn-IN',
};
const PLANET_SHORT_NAMES = {
  sun: 'Su',
  moon: 'Mo',
  mars: 'Ma',
  mercury: 'Me',
  jupiter: 'Ju',
  venus: 'Ve',
  saturn: 'Sa',
  rahu: 'Ra',
  ketu: 'Ke',
  ascendant: 'Asc',
  lagna: 'Asc'
};
const SIGN_NUMBER_BY_NAME = {
  aries: 1,
  taurus: 2,
  gemini: 3,
  cancer: 4,
  leo: 5,
  virgo: 6,
  libra: 7,
  scorpio: 8,
  sagittarius: 9,
  capricorn: 10,
  aquarius: 11,
  pisces: 12,
  mesh: 1,
  vrishabh: 2,
  mithun: 3,
  kark: 4,
  simha: 5,
  kanya: 6,
  tula: 7,
  vrischik: 8,
  dhanu: 9,
  makar: 10,
  kumbh: 11,
  meen: 12
};
const NORTH_INDIAN_HOUSE_LAYOUT = [
  { house: 1, signTop: '45%', signLeft: '50%', planetTop: '21%', planetLeft: '50%' },
  { house: 2, signTop: '21%', signLeft: '26%', planetTop: '13%', planetLeft: '20%' },
  { house: 3, signTop: '28%', signLeft: '19%', planetTop: '30%', planetLeft: '12%' },
  { house: 4, signTop: '50%', signLeft: '42%', planetTop: '49%', planetLeft: '22%' },
  { house: 5, signTop: '75%', signLeft: '22%', planetTop: '74%', planetLeft: '9%' },
  { house: 6, signTop: '81%', signLeft: '26%', planetTop: '94%', planetLeft: '22%' },
  { house: 7, signTop: '56%', signLeft: '50%', planetTop: '74%', planetLeft: '50%' },
  { house: 8, signTop: '80%', signLeft: '74%', planetTop: '90%', planetLeft: '78%' },
  { house: 9, signTop: '74%', signLeft: '79%', planetTop: '74%', planetLeft: '89%' },
  { house: 10, signTop: '50%', signLeft: '58%', planetTop: '49%', planetLeft: '74%' },
  { house: 11, signTop: '27%', signLeft: '81%', planetTop: '30%', planetLeft: '88%' },
  { house: 12, signTop: '21%', signLeft: '73%', planetTop: '13%', planetLeft: '80%' }
];

const resolveHouseNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const numeric = Math.trunc(value);
    return numeric >= 1 && numeric <= 12 ? numeric : null;
  }

  const raw = String(value || '').trim();
  if (!raw) return null;
  const match = raw.match(/\d+/);
  if (!match) return null;
  const numeric = parseInt(match[0], 10);
  return numeric >= 1 && numeric <= 12 ? numeric : null;
};

const toPlanetShortName = (name) => {
  const raw = String(name || '').trim();
  if (!raw) return '';
  const normalized = raw.toLowerCase();
  if (PLANET_SHORT_NAMES[normalized]) return PLANET_SHORT_NAMES[normalized];
  if (raw.length <= 4) return raw;
  return raw.slice(0, 3);
};

const buildVedicChartHouses = (kundliResult) => {
  const apiPlanets = Array.isArray(kundliResult?.kundliData?.planets)
    ? kundliResult.kundliData.planets
    : null;
  const fallbackPlacements = Array.isArray(kundliResult?.keyPlacements)
    ? kundliResult.keyPlacements
    : [];
  const placements = apiPlanets || fallbackPlacements;
  const houses = Array.from({ length: 12 }, () => []);

  placements.forEach((item) => {
    const house = resolveHouseNumber(item?.house);
    if (!house) return;
    let shortName = toPlanetShortName(item?.name);
    if (!shortName) return;
    const isRetrograde = item?.isRetro === true || item?.is_retro === true || item?.retrograde === true;
    if (isRetrograde) shortName = `${shortName}(R)`;
    houses[house - 1].push(shortName);
  });

  return houses.map((entry) => entry.join('\n'));
};

const resolveAscendantSignNumber = (kundliResult) => {
  const ascendantRaw = String(
    kundliResult?.summary?.ascendant
      || kundliResult?.kundliData?.astro_details?.ascendant
      || ''
  ).toLowerCase().trim();

  if (!ascendantRaw) return null;

  const numericMatch = ascendantRaw.match(/\d+/);
  if (numericMatch) {
    const numeric = parseInt(numericMatch[0], 10);
    if (numeric >= 1 && numeric <= 12) return numeric;
  }

  const cleaned = ascendantRaw.replace(/[^a-z]/g, '');
  return SIGN_NUMBER_BY_NAME[cleaned] || null;
};

const buildNorthIndianSignNumbers = (kundliResult) => {
  const lagnaSign = resolveAscendantSignNumber(kundliResult);
  if (!lagnaSign) return Array.from({ length: 12 }, (_, idx) => idx + 1);
  return Array.from({ length: 12 }, (_, idx) => ((lagnaSign + idx - 1) % 12) + 1);
};

const REMEDIES = [
  {
    id: 'wealthFinancialProsperity',
    title: 'Wealth And Financial Prosperity',
    category: 'Wealth',
    desc: 'For attaining financial stability, business success, and material prosperity',
    deity: 'Lakshmi',
    deityDesc: 'Lakshmi is the supreme source of all wealth, abundance, and prosperity. She blesses with financial success and material comfort.',
    poojas: ['Lakshmi-Archana', 'Lakshmi-Homam', 'Lakshmi-Sahasranam', 'Kuber-Pooja'],
  },
  {
    id: 'familyHarmonyHouseholdPeace',
    title: 'Family Harmony And Household Peace',
    category: 'Family',
    desc: 'For creating peaceful home environment, resolving family conflicts, and maintaining domestic harmony',
    deity: 'Annapurna',
    deityDesc: 'Annapurna symbolizes nurturing, care, and family well-being. She ensures household harmony and prosperity.',
    poojas: ['Annapurna-Archana', 'Durga-Saptashati', 'Ganesha-Maha-Puja', 'House-Blessing-Havan'],
  },
  {
    id: 'academicExcellenceKnowledge',
    title: 'Academic Excellence And Knowledge',
    category: 'Education',
    desc: 'For success in studies, exams, and acquiring deep knowledge and wisdom',
    deity: 'Saraswati',
    deityDesc: 'Saraswati is the goddess of knowledge, music, and learning. She bestows wisdom, intellect, and academic success.',
    poojas: ['Saraswati-Pooja', 'Hayagriva-Homam', 'Vidya-Lakshmi-Pooja', 'Aksharabhyasam'],
  },
  {
    id: 'careerGrowthProfessionalSuccess',
    title: 'Career Growth And Professional Success',
    category: 'Career',
    desc: 'For job promotions, business growth, removing professional obstacles, and achieving career goals',
    deity: 'Ganesha',
    deityDesc: 'Ganesha is the remover of obstacles and the lord of new beginnings. He clears the path for career advancement.',
    poojas: ['Ganesha-Homam', 'Navagraha-Pooja', 'Vishnu-Sahasranam', 'Shani-Pooja'],
  },
  {
    id: 'marriageRelationshipBliss',
    title: 'Marriage And Relationship Bliss',
    category: 'Marriage',
    desc: 'For finding the right partner, happy married life, and resolving relationship issues',
    deity: 'Parvati',
    deityDesc: 'Parvati is the goddess of love, devotion, and marital harmony. She blesses with strong and lasting relationships.',
    poojas: ['Swayamvara-Parvati-Homam', 'Mangala-Gauri-Pooja', 'Vivah-Panchami-Pooja', 'Katyayani-Pooja'],
  },
  {
    id: 'healthHealing',
    title: 'Health And Healing',
    category: 'Health',
    desc: 'For recovery from illness, maintaining good health, longevity, and overall physical well-being',
    deity: 'Dhanvantari',
    deityDesc: 'Dhanvantari is the divine physician and god of Ayurveda. He grants healing, health, and freedom from disease.',
    poojas: ['Maha-Mrityunjaya-Jaap', 'Dhanvantari-Homam', 'Sudarshana-Homam', 'Ayush-Homam'],
  },
  {
    id: 'protectionSafety',
    title: 'Protection And Safety',
    category: 'Protection',
    desc: 'For shielding from negative energies, evil eye, and ensuring safety of family and property',
    deity: 'Hanuman',
    deityDesc: 'Hanuman is the embodiment of strength, courage, and devotion. He protects devotees from all dangers and evil forces.',
    poojas: ['Hanuman-Chalisa-Path', 'Sudarshana-Homam', 'Durga-Kavach-Path', 'Baglamukhi-Pooja'],
  },
  {
    id: 'childrenProgeny',
    title: 'Children And Progeny',
    category: 'Children',
    desc: 'For blessed parenthood, child health, academic success of children, and their overall well-being',
    deity: 'Santoshi Maa',
    deityDesc: 'Santoshi Maa is the goddess of satisfaction and happiness. She blesses families with healthy children and contentment.',
    poojas: ['Santana-Gopala-Homam', 'Putrakameshti-Yajna', 'Garba-Raksha-Pooja', 'Santoshi-Maa-Vrat'],
  },
  {
    id: 'spiritualAwakeningInnerPeace',
    title: 'Spiritual Awakening And Inner Peace',
    category: 'Spiritual Growth',
    desc: 'For deepening meditation, spiritual progress, attaining inner peace, and self-realization',
    deity: 'Shiva',
    deityDesc: 'Shiva is the supreme consciousness, the lord of meditation and transformation. He grants liberation and inner peace.',
    poojas: ['Rudra-Abhishek', 'Maha-Shivaratri-Pooja', 'Lingashtakam-Path', 'Dhyana-Pooja'],
  },
];

const normalizeResponseText = (value) => {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  return String(value);
};

const cleanMarkdownArtifacts = (text) => (
  normalizeResponseText(text)
    // Convert star bullets to hyphen bullets for cleaner parsing/display
    .replace(/^\s*\*\s+/gm, '- ')
    // Remove heading markers that may appear mid-block/inline
    .replace(/^\s{0,3}#{1,6}\s*/gm, '')
    // Remove isolated markdown marker tokens that leak into output
    .replace(/(^|\s)[#*]+(?=\s|$)/g, '$1')
);

const renderInlineFormatting = (text, keyPrefix = 'inline') => {
  const raw = cleanMarkdownArtifacts(text);
  const parts = raw.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*)/g).filter(Boolean);

  return parts.map((part, index) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return (
        <strong key={`${keyPrefix}-strong-${index}`}>
          {part.slice(2, -2)}
        </strong>
      );
    }

    if (/^\*[^*\n]+\*$/.test(part)) {
      return (
        <em key={`${keyPrefix}-em-${index}`}>
          {part.slice(1, -1)}
        </em>
      );
    }

    return <React.Fragment key={`${keyPrefix}-text-${index}`}>{part}</React.Fragment>;
  });
};

const renderRichResponseText = (content, keyPrefix = 'response') => {
  const normalized = normalizeResponseText(content).replace(/\r\n/g, '\n').trim();
  if (!normalized) return null;

  const rawBlocks = normalized
    .split(/\n\s*\n+/)
    .map((block) => block.trim())
    .filter(Boolean);

  // Merge consecutive list-only blocks so numbering/bullets do not reset
  // when the model inserts blank lines between items.
  const blocks = [];
  rawBlocks.forEach((block) => {
    const lines = block
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    const isOrderedListBlock = lines.length > 0 && lines.every((line) => /^\d+\.\s+/.test(line));
    const isUnorderedListBlock = lines.length > 0 && lines.every((line) => /^[-*]\s+/.test(line));
    const previous = blocks[blocks.length - 1];

    if (isOrderedListBlock && previous?.type === 'ordered-list') {
      previous.content = `${previous.content}\n${block}`;
      return;
    }

    if (isUnorderedListBlock && previous?.type === 'unordered-list') {
      previous.content = `${previous.content}\n${block}`;
      return;
    }

    if (isOrderedListBlock) {
      blocks.push({ type: 'ordered-list', content: block });
      return;
    }

    if (isUnorderedListBlock) {
      blocks.push({ type: 'unordered-list', content: block });
      return;
    }

    blocks.push({ type: 'text', content: block });
  });

  return blocks.map((entry, blockIndex) => {
    const block = entry.content;
    const takeawayMatch = block.match(/^key\s*takeaway\s*:\s*(.+)$/is);
    if (takeawayMatch) {
      return (
        <div className="ai-response-callout" key={`${keyPrefix}-callout-${blockIndex}`}>
          <span className="ai-response-callout-label">Key Takeaway</span>
          <p className="ai-response-callout-text">
            {renderInlineFormatting(takeawayMatch[1].trim(), `${keyPrefix}-callout-text-${blockIndex}`)}
          </p>
        </div>
      );
    }

    const headingMatch = block.match(/^(#{1,6})\s*(.+)$/);
    if (headingMatch) {
      return (
        <h3 className="ai-response-heading" key={`${keyPrefix}-heading-${blockIndex}`}>
          {renderInlineFormatting(headingMatch[2].trim(), `${keyPrefix}-heading-text-${blockIndex}`)}
        </h3>
      );
    }

    const lines = block
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    const orderedLines = lines.filter((line) => /^\d+\.\s+/.test(line));
    if (lines.length > 0 && orderedLines.length === lines.length) {
      return (
        <ol className="ai-response-list ai-response-list-ordered" key={`${keyPrefix}-ol-${blockIndex}`}>
          {lines.map((line, itemIndex) => (
            <li key={`${keyPrefix}-ol-${blockIndex}-${itemIndex}`}>
              {renderInlineFormatting(line.replace(/^\d+\.\s+/, '').trim(), `${keyPrefix}-ol-item-${blockIndex}-${itemIndex}`)}
            </li>
          ))}
        </ol>
      );
    }

    const unorderedLines = lines.filter((line) => /^[-*]\s+/.test(line));
    if (lines.length > 0 && unorderedLines.length === lines.length) {
      return (
        <ul className="ai-response-list ai-response-list-unordered" key={`${keyPrefix}-ul-${blockIndex}`}>
          {lines.map((line, itemIndex) => (
            <li key={`${keyPrefix}-ul-${blockIndex}-${itemIndex}`}>
              {renderInlineFormatting(line.replace(/^[-*]\s+/, '').trim(), `${keyPrefix}-ul-item-${blockIndex}-${itemIndex}`)}
            </li>
          ))}
        </ul>
      );
    }

    return (
      <p className="ai-response-paragraph" key={`${keyPrefix}-p-${blockIndex}`}>
        {renderInlineFormatting(block, `${keyPrefix}-paragraph-${blockIndex}`)}
      </p>
    );
  });
};


const BhaktiSamadhan = () => {
  const { isLoggedIn } = useAuth();
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('advisor');
  const imageUrl = '/images/bg.avif';

  // Advisor state
  const [advisorInput, setAdvisorInput] = useState('');
  const [advisorResult, setAdvisorResult] = useState('');
  const [advisorLoading, setAdvisorLoading] = useState(false);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All Remedies');
  const [chatHistory, setChatHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [guestLimitInfo, setGuestLimitInfo] = useState(DEFAULT_GUEST_LIMIT_INFO);
  const [kundliForm, setKundliForm] = useState({
    name: '',
    dateOfBirth: '',
    timeOfBirth: '',
    birthPlace: '',
    latitude: '',
    longitude: '',
    timezone: '5.5'
  });
  const [kundliLoading, setKundliLoading] = useState(false);
  const [kundliResult, setKundliResult] = useState(null);
  const [kundliError, setKundliError] = useState('');
  const [astrologyStatusLoading, setAstrologyStatusLoading] = useState(false);
  const [astrologyStatus, setAstrologyStatus] = useState({
    ready: true,
    message: ''
  });
  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const chatMessagesAreaRef = useRef(null);
  const loadedFromHistoryRef = useRef(false);
  const isGuestLimitReached = !isLoggedIn && guestLimitInfo.isReached;
  const kundliHousePlanets = buildVedicChartHouses(kundliResult);
  const kundliSignNumbers = buildNorthIndianSignNumbers(kundliResult);

  const getCategoryLabel = (category) => t(CATEGORY_TRANSLATION_KEYS[category], {}, category);

  const getLocalizedRemedy = (remedy) => {
    const key = `bhaktiSamadhan.remedies.${remedy.id}`;
    return {
      ...remedy,
      title: t(`${key}.title`, {}, remedy.title),
      desc: t(`${key}.desc`, {}, remedy.desc),
      deity: t(`${key}.deity`, {}, remedy.deity),
      deityDesc: t(`${key}.deityDesc`, {}, remedy.deityDesc),
      poojas: t(`${key}.poojas`, {}, remedy.poojas),
    };
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    document.body.classList.add('bhakti-samadhan-theme');
    return () => {
      document.body.classList.remove('bhakti-samadhan-theme');
    };
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      setGuestLimitInfo(DEFAULT_GUEST_LIMIT_INFO);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const chatArea = chatMessagesAreaRef.current;
    if (!chatArea) return;
    const lastMessage = messages[messages.length - 1];
    let rafId;

    if (loadedFromHistoryRef.current) {
      chatArea.scrollTo({ top: 0, behavior: 'smooth' });
      loadedFromHistoryRef.current = false;
    } else if (chatLoading) {
      // While waiting, keep the latest user message and processing bubble visible.
      chatArea.scrollTo({ top: chatArea.scrollHeight, behavior: 'smooth' });
    } else if (lastMessage?.role === 'assistant') {
      // Once final response is rendered, show it from the beginning.
      rafId = requestAnimationFrame(() => {
        const assistantMessages = chatArea.querySelectorAll('.chat-msg.assistant');
        const latestAssistantMessage = assistantMessages[assistantMessages.length - 1];
        if (!latestAssistantMessage) return;

        // Align chat viewport to the very beginning of latest assistant response.
        const targetTop = Math.max(0, latestAssistantMessage.offsetTop - chatArea.offsetTop);
        chatArea.scrollTo({ top: targetTop, behavior: 'smooth' });
      });
    } else {
      // Default fallback for non-assistant terminal updates.
      chatArea.scrollTo({ top: chatArea.scrollHeight, behavior: 'smooth' });
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [messages, chatLoading]);

  // Load chat history when switching to chat tab (logged-in users only)
  useEffect(() => {
    if (activeTab === 'chat' && isLoggedIn) {
      loadChatHistory();
    }
  }, [activeTab, isLoggedIn]);

  useEffect(() => {
    if (activeTab !== 'kundli') return;
    let isMounted = true;

    const loadAstrologyStatus = async () => {
      setAstrologyStatusLoading(true);
      try {
        const response = await apiService.getAstrologyStatus();
        if (!isMounted) return;
        if (response?.success) {
          setAstrologyStatus({
            ready: !!response.ready,
            message: response.message || ''
          });
          if (!response.ready) {
            setKundliError(response.message || 'Astrology API is currently unavailable.');
          }
        }
      } catch (error) {
        if (!isMounted) return;
        setAstrologyStatus({
          ready: false,
          message: error?.message || 'Failed to fetch astrology status.'
        });
      } finally {
        if (isMounted) setAstrologyStatusLoading(false);
      }
    };

    loadAstrologyStatus();
    return () => {
      isMounted = false;
    };
  }, [activeTab]);

  const loadChatHistory = async () => {
    if (!isLoggedIn) return;
    setHistoryLoading(true);
    try {
      const response = await apiService.getAIConversations();
      if (response.success) {
        setChatHistory(response.conversations || []);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadConversation = async (convId) => {
    if (convId === conversationId) {
      setShowHistory(false);
      return;
    }
    try {
      const response = await apiService.getAIConversation(convId);
      if (response.success && response.conversation) {
        loadedFromHistoryRef.current = true;
        setConversationId(convId);
        setMessages(
          response.conversation.messages.map((m) => ({
            role: m.role,
            content: m.content,
          }))
        );
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
    setShowHistory(false);
  };

  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    try {
      await apiService.deleteAIConversation(convId);
      setChatHistory((prev) => prev.filter((c) => c._id !== convId));
      if (conversationId === convId) {
        startNewChat();
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  // --- Advisor ---
  const handleGetRecommendation = async () => {
    const trimmed = advisorInput.trim();
    if (!trimmed || advisorLoading) return;
    if (isGuestLimitReached) {
      setAdvisorResult(guestLimitInfo.message);
      return;
    }

    setAdvisorLoading(true);
    setAdvisorResult('');
    try {
      const response = await apiService.getAIRecommendation(trimmed, language);
      if (response.success) {
        const recs = response.data?.aiResponse || response.data?.recommendations;
        if (recs && Array.isArray(recs) && recs.length > 0) {
          // Use the description field which now contains rich text advice
          const advice = recs.map((rec) => rec.description || '').filter(Boolean).join('\n\n');
          setAdvisorResult(advice || t('bhaktiSamadhan.advisor.noRecommendation'));
        } else {
          setAdvisorResult(response.response || t('bhaktiSamadhan.advisor.noRecommendation'));
        }
      }
    } catch (error) {
      if (isGuestLimitError(error)) {
        const limitMessage = getGuestLimitMessage(error);
        setLimitStateFromError(error, limitMessage);
        setAdvisorResult(limitMessage);
        return;
      }

      console.error('Advisor error:', error);
      setAdvisorResult(t('bhaktiSamadhan.advisor.genericError'));
    } finally {
      setAdvisorLoading(false);
    }
  };

  // --- Chat ---
  const handleSendChat = async (e) => {
    e.preventDefault();
    const trimmed = chatInput.trim();
    if (!trimmed || chatLoading) return;
    if (isGuestLimitReached) {
      setMessages((prev) => {
        const lastMessage = prev[prev.length - 1];
        if (lastMessage?.role === 'assistant' && lastMessage?.content === guestLimitInfo.message) {
          return prev;
        }
        return [...prev, { role: 'assistant', content: guestLimitInfo.message }];
      });
      return;
    }

    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setChatInput('');
    setChatLoading(true);

    try {
      const response = await apiService.sendAIMessage(trimmed, conversationId, language);
      if (response.success) {
        setConversationId(response.conversationId || conversationId);
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: response.response || response.message },
        ]);
      }
    } catch (error) {
      if (isGuestLimitError(error)) {
        const limitMessage = getGuestLimitMessage(error);
        setLimitStateFromError(error, limitMessage);
        setMessages((prev) => [...prev, { role: 'assistant', content: limitMessage }]);
        return;
      }

      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: t('bhaktiSamadhan.chat.genericError') },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  const startNewChat = () => {
    setConversationId(null);
    setMessages([]);
    chatInputRef.current?.focus();
  };

  // Refresh chat history after sending a message (for new conversations)
  const handleSendChatWithHistory = async (e) => {
    await handleSendChat(e);
    if (isLoggedIn) {
      loadChatHistory();
    }
  };

  const formatResetTime = (isoTime) => {
    if (!isoTime) return '';
    const resetDate = new Date(isoTime);
    if (Number.isNaN(resetDate.getTime())) return '';
    const locale = LANGUAGE_LOCALE_MAP[language] || LANGUAGE_LOCALE_MAP.en;
    return resetDate.toLocaleString(locale);
  };

  const getGuestLimitMessage = (error) => {
    const localizedBaseMessage = t('bhaktiSamadhan.guest.defaultLimitMessage');
    const backendMessage = error?.message;
    const quotaLimit = error?.quotaLimit || error?.details?.quotaLimit || 3;
    const backendMessageMentionsCurrentLimit = typeof backendMessage === 'string'
      && backendMessage.includes(`${quotaLimit} requests per day`);
    const baseMessage = language === 'en' && backendMessageMentionsCurrentLimit
      ? backendMessage
      : localizedBaseMessage;
    const resetTime = error?.quotaResetTime || error?.details?.quotaResetTime;
    const formattedResetTime = formatResetTime(resetTime);
    return formattedResetTime
      ? t('bhaktiSamadhan.guest.tryAgainAfter', { baseMessage, time: formattedResetTime })
      : baseMessage;
  };

  const isGuestLimitError = (error) => {
    const action = error?.action || error?.details?.action;
    const message = (error?.message || '').toLowerCase();
    const actionMatched = action === 'signup_required' || action === 'login_or_signup_required';
    const messageMatched = message.includes('daily limit') || message.includes('3 requests per day') || message.includes('guest user');
    return !isLoggedIn && (actionMatched || (error?.status === 429 && messageMatched));
  };

  const setLimitStateFromError = (error, message) => {
    setGuestLimitInfo({
      isReached: true,
      message,
      quotaLimit: error?.quotaLimit || error?.details?.quotaLimit || 3,
      quotaUsed: error?.quotaUsed || error?.details?.quotaUsed || 3,
      quotaResetTime: error?.quotaResetTime || error?.details?.quotaResetTime || ''
    });
  };

  const handleKundliInputChange = (field, value) => {
    setKundliForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const buildKundliPayload = () => {
    const optionalNumber = (value) => {
      const raw = String(value ?? '').trim();
      if (!raw) return undefined;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : undefined;
    };

    const payload = {
      name: kundliForm.name.trim(),
      dateOfBirth: kundliForm.dateOfBirth.trim(),
      timeOfBirth: kundliForm.timeOfBirth.trim(),
      birthPlace: kundliForm.birthPlace.trim(),
      language
    };

    const latitude = optionalNumber(kundliForm.latitude);
    const longitude = optionalNumber(kundliForm.longitude);
    const timezone = optionalNumber(kundliForm.timezone);

    if (latitude !== undefined) payload.latitude = latitude;
    if (longitude !== undefined) payload.longitude = longitude;
    if (timezone !== undefined) payload.timezone = timezone;

    return payload;
  };

  const timezoneOptions = [
    { value: '-12', label: '(UTC-12:00) International Date Line West' },
    { value: '-11', label: '(UTC-11:00) Coordinated Universal Time-11' },
    { value: '-10', label: '(UTC-10:00) Hawaii' },
    { value: '-9.5', label: '(UTC-09:30) Marquesas Islands' },
    { value: '-9', label: '(UTC-09:00) Alaska' },
    { value: '-8', label: '(UTC-08:00) Pacific Time' },
    { value: '-7', label: '(UTC-07:00) Mountain Time' },
    { value: '-6', label: '(UTC-06:00) Central Time' },
    { value: '-5', label: '(UTC-05:00) Eastern Time' },
    { value: '-4', label: '(UTC-04:00) Atlantic Time' },
    { value: '-3.5', label: '(UTC-03:30) Newfoundland' },
    { value: '-3', label: '(UTC-03:00) Argentina, Brazil' },
    { value: '-2', label: '(UTC-02:00) Mid-Atlantic' },
    { value: '-1', label: '(UTC-01:00) Azores' },
    { value: '0', label: '(UTC+00:00) London, UTC' },
    { value: '1', label: '(UTC+01:00) Central Europe' },
    { value: '2', label: '(UTC+02:00) Eastern Europe' },
    { value: '3', label: '(UTC+03:00) Moscow, East Africa' },
    { value: '3.5', label: '(UTC+03:30) Tehran' },
    { value: '4', label: '(UTC+04:00) Dubai' },
    { value: '4.5', label: '(UTC+04:30) Kabul' },
    { value: '5', label: '(UTC+05:00) Pakistan' },
    { value: '5.5', label: '(UTC+05:30) India (IST)' },
    { value: '5.75', label: '(UTC+05:45) Nepal' },
    { value: '6', label: '(UTC+06:00) Bangladesh' },
    { value: '6.5', label: '(UTC+06:30) Myanmar' },
    { value: '7', label: '(UTC+07:00) Thailand, Vietnam' },
    { value: '8', label: '(UTC+08:00) China, Singapore' },
    { value: '8.75', label: '(UTC+08:45) Australian Central Western' },
    { value: '9', label: '(UTC+09:00) Japan, Korea' },
    { value: '9.5', label: '(UTC+09:30) Australian Central' },
    { value: '10', label: '(UTC+10:00) Australian Eastern' },
    { value: '10.5', label: '(UTC+10:30) Lord Howe Island' },
    { value: '11', label: '(UTC+11:00) Solomon Islands' },
    { value: '12', label: '(UTC+12:00) New Zealand, Fiji' },
    { value: '12.75', label: '(UTC+12:45) Chatham Islands' },
    { value: '13', label: '(UTC+13:00) Samoa, Tonga' },
    { value: '14', label: '(UTC+14:00) Line Islands' }
  ];

  const handleAnalyzeKundli = async (e) => {
    e.preventDefault();
    if (kundliLoading) return;
    if (!astrologyStatus.ready) {
      setKundliError(astrologyStatus.message || 'Astrology API is currently unavailable.');
      return;
    }
    if (
      kundliForm.latitude.trim() === '' ||
      kundliForm.longitude.trim() === '' ||
      kundliForm.timezone.trim() === ''
    ) {
      setKundliError('For API-based kundli generation, please provide latitude, longitude, and timezone.');
      return;
    }
    if (isGuestLimitReached) {
      setKundliError(guestLimitInfo.message);
      return;
    }

    setKundliLoading(true);
    setKundliError('');
    setKundliResult(null);

    try {
      const payload = buildKundliPayload();
      const response = await apiService.analyzeKundli(payload);
      if (response?.success) {
        setKundliResult(response);
      } else {
        setKundliError(response?.message || t('bhaktiSamadhan.chat.genericError'));
      }
    } catch (error) {
      if (isGuestLimitError(error)) {
        const limitMessage = getGuestLimitMessage(error);
        setLimitStateFromError(error, limitMessage);
        setKundliError(limitMessage);
        return;
      }
      const rawMessage = error?.message || t('bhaktiSamadhan.chat.genericError');
      const isAstroUpstreamIssue = rawMessage.toLowerCase().includes('did not return usable data') || error?.status === 502;
      setKundliError(
        isAstroUpstreamIssue
          ? 'Astrology API could not process this input right now. Please verify birth place/date/time (or coordinates/timezone), and confirm your AstrologyAPI account credits/endpoint access.'
          : rawMessage
      );
    } finally {
      setKundliLoading(false);
    }
  };

  return (
    <>
      <PageBackground imageUrl={imageUrl} />
      <div className="bhakti-samadhan-page">
        {/* Hero Header */}
        <div className="samadhan-hero">
          <span className="samadhan-om-icon">ॐ</span>
          <h1 className="samadhan-hero-title">{t('bhaktiSamadhan.hero.title')}</h1>
          <p className="samadhan-hero-subtitle">
            {t('bhaktiSamadhan.hero.subtitle')}
          </p>
        </div>

        {/* Tab Buttons */}
        <div className="samadhan-tabs">
          <button
            className={`samadhan-tab ${activeTab === 'advisor' ? 'active' : ''}`}
            onClick={() => setActiveTab('advisor')}
          >
            <i className="fas fa-paper-plane"></i> {t('bhaktiSamadhan.tabs.askAdvisor')}
          </button>
          <button
            className={`samadhan-tab ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => setActiveTab('chat')}
          >
            <i className="fas fa-comments"></i> {t('bhaktiSamadhan.tabs.chat')}
          </button>
          <button
            className={`samadhan-tab ${activeTab === 'kundli' ? 'active' : ''}`}
            onClick={() => setActiveTab('kundli')}
          >
            <i className="fas fa-chart-pie"></i> {t('bhaktiSamadhan.tabs.kundli', {}, 'Kundli')}
          </button>
          <button
            className={`samadhan-tab ${activeTab === 'remedies' ? 'active' : ''}`}
            onClick={() => setActiveTab('remedies')}
          >
            <i className="fas fa-search"></i> {t('bhaktiSamadhan.tabs.browseRemedies')}
          </button>
        </div>

        {/* Tab Content */}
        <div className="samadhan-content">
          {/* === ASK ADVISOR TAB === */}
          {activeTab === 'advisor' && (
            <div className="advisor-section">
              <div className="advisor-card">
                <h2 className="advisor-card-title">{t('bhaktiSamadhan.advisor.describeTitle')}</h2>
                <p className="advisor-card-desc">
                  {t('bhaktiSamadhan.advisor.describeDesc')}
                </p>
                <textarea
                  className="advisor-textarea"
                  placeholder={t('bhaktiSamadhan.advisor.placeholder')}
                  value={advisorInput}
                  onChange={(e) => setAdvisorInput(e.target.value)}
                  rows={5}
                  maxLength={2000}
                  disabled={advisorLoading}
                />
                <button
                  className="advisor-submit-btn"
                  onClick={handleGetRecommendation}
                  disabled={!advisorInput.trim() || advisorLoading || isGuestLimitReached}
                >
                  {advisorLoading ? (
                    <><i className="fas fa-spinner fa-spin"></i> {t('bhaktiSamadhan.advisor.gettingRecommendation')}</>
                  ) : (
                    t('bhaktiSamadhan.advisor.getRecommendation')
                  )}
                </button>
                {isGuestLimitReached && (
                  <div className="guest-limit-cta">
                    <p>{guestLimitInfo.message}</p>
                    <div className="guest-limit-actions">
                      <Link to="/login" className="guest-limit-link">{t('nav.login')}</Link>
                      <Link to="/signup" className="guest-limit-link secondary">{t('nav.signUp')}</Link>
                    </div>
                  </div>
                )}

                {/* Result */}
                <div className={`advisor-result ${advisorResult ? 'has-content' : ''}`}>
                  {advisorLoading && (
                    <div className="advisor-loading">
                      <div className="advisor-loading-dots">
                        <span></span><span></span><span></span>
                      </div>
                      <p>{t('bhaktiSamadhan.advisor.consultingWisdom')}</p>
                    </div>
                  )}
                  {advisorResult && !advisorLoading && (
                    <div className="advisor-response">
                      <h3><i className="fas fa-om"></i> {t('bhaktiSamadhan.advisor.spiritualGuidance')}</h3>
                      <div className="advisor-response-text">
                        {(() => {
                          const text = typeof advisorResult === 'string' ? advisorResult : JSON.stringify(advisorResult, null, 2);
                          const citationSplit = text.split(/\n---\n/);
                          const mainContent = citationSplit[0].trim();
                          const citations = citationSplit.length > 1 ? citationSplit.slice(1).join('\n').trim() : null;
                          return (
                            <>
                              <div className="ai-response-rich">
                                {renderRichResponseText(mainContent, 'advisor-main')}
                              </div>
                              {citations && (
                                <div className="chat-citations">
                                  {citations.split('\n').map((line, i) => (
                                    <p key={i}>{line}</p>
                                  ))}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* === CHAT TAB === */}
          {activeTab === 'chat' && (
            <div className={`chat-section ${showHistory ? 'history-open' : ''}`}>
              {/* Chat History Sidebar */}
              {isLoggedIn && showHistory && (
                <div className="chat-history-sidebar">
                  <div className="chat-history-header">
                    <h3>{t('bhaktiSamadhan.chat.historyTitle')}</h3>
                    <button className="chat-history-close" onClick={() => setShowHistory(false)}>
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                  <button className="chat-history-new-btn" onClick={() => { startNewChat(); setShowHistory(false); }}>
                    <i className="fas fa-plus"></i> {t('bhaktiSamadhan.chat.newChat')}
                  </button>
                  <div className="chat-history-list">
                    {historyLoading ? (
                      <div className="chat-history-loading">{t('bhaktiSamadhan.chat.loading')}</div>
                    ) : chatHistory.length === 0 ? (
                      <div className="chat-history-empty">{t('bhaktiSamadhan.chat.noConversations')}</div>
                    ) : (
                      chatHistory.map((conv) => (
                        <div
                          key={conv._id}
                          className={`chat-history-item ${conv._id === conversationId ? 'active' : ''}`}
                          onClick={() => loadConversation(conv._id)}
                        >
                          <div className="chat-history-item-info">
                            <span className="chat-history-item-title">{conv.title || t('bhaktiSamadhan.chat.untitled')}</span>
                            <span className="chat-history-item-date">
                              {new Date(conv.lastMessageAt || conv.createdAt).toLocaleDateString(LANGUAGE_LOCALE_MAP[language] || LANGUAGE_LOCALE_MAP.en)}
                            </span>
                          </div>
                          <button
                            className="chat-history-item-delete"
                            onClick={(e) => deleteConversation(conv._id, e)}
                            title={t('bhaktiSamadhan.chat.deleteConversation')}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              <div className="chat-card">
                <div className="chat-card-header">
                  <div className="chat-header-left">
                    {isLoggedIn && (
                      <button className="chat-history-toggle" onClick={() => setShowHistory(!showHistory)} title={t('bhaktiSamadhan.chat.historyTitle')}>
                        <i className="fas fa-history"></i>
                      </button>
                    )}
                    <h2>{t('bhaktiSamadhan.chat.spiritualChat')}</h2>
                  </div>
                  {messages.length > 0 && (
                    <button className="chat-new-btn" onClick={startNewChat}>
                      <i className="fas fa-plus"></i> {t('bhaktiSamadhan.chat.newChat')}
                    </button>
                  )}
                </div>

                <div className="chat-messages-area" ref={chatMessagesAreaRef}>
                  {messages.length === 0 ? (
                    <div className="chat-empty">
                      <div className="chat-empty-icon">🙏</div>
                      <h3>{t('bhaktiSamadhan.chat.namaste')}</h3>
                      <p>{t('bhaktiSamadhan.chat.emptyPrompt')}</p>
                      {!isLoggedIn && (
                        <p className="guest-badge">
                          <i className="fas fa-info-circle"></i> {t('bhaktiSamadhan.chat.guestModeInfo')}
                        </p>
                      )}
                      <div className="chat-suggestions">
                        {t('bhaktiSamadhan.chat.suggestions', {}, []).map((q, i) => (
                          <button
                            key={i}
                            className="chat-suggestion-btn"
                            onClick={() => { setChatInput(q); chatInputRef.current?.focus(); }}
                            disabled={isGuestLimitReached}
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    messages.map((msg, idx) => {
                      // Split citations from main content for assistant messages
                      let mainContent = msg.content;
                      let citations = null;
                      if (msg.role === 'assistant') {
                        const citationSplit = msg.content.split(/\n---\n/);
                        if (citationSplit.length > 1) {
                          mainContent = citationSplit[0].trim();
                          citations = citationSplit.slice(1).join('\n').trim();
                        }
                      }
                      return (
                        <div key={idx} className={`chat-msg ${msg.role}`}>
                          <div className="chat-msg-avatar">
                            {msg.role === 'user' ? <i className="fas fa-user"></i> : <span>ॐ</span>}
                          </div>
                          <div className="chat-msg-bubble">
                            {msg.role === 'assistant' ? (
                              <div className="ai-response-rich">
                                {renderRichResponseText(mainContent, `assistant-${idx}`)}
                              </div>
                            ) : (
                              mainContent.split('\n').map((line, i) => (
                                <p key={i}>{line}</p>
                              ))
                            )}
                            {citations && (
                              <div className="chat-citations">
                                {citations.split('\n').map((line, i) => (
                                  <p key={i}>{line}</p>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                  {chatLoading && (
                    <div className="chat-msg assistant">
                      <div className="chat-msg-avatar"><span>ॐ</span></div>
                      <div className="chat-msg-bubble typing">
                        <span className="dot"></span><span className="dot"></span><span className="dot"></span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
                {isGuestLimitReached && (
                  <div className="guest-limit-cta chat-guest-limit-cta">
                    <p>{guestLimitInfo.message}</p>
                    <div className="guest-limit-actions">
                      <Link to="/login" className="guest-limit-link">{t('nav.login')}</Link>
                      <Link to="/signup" className="guest-limit-link secondary">{t('nav.signUp')}</Link>
                    </div>
                  </div>
                )}

                <form className="chat-input-bar" onSubmit={handleSendChatWithHistory}>
                  <input
                    className="chat-question-input"
                    ref={chatInputRef}
                    type="text"
                    placeholder={isGuestLimitReached ? t('bhaktiSamadhan.guest.defaultLimitMessage') : t('bhaktiSamadhan.chat.askPlaceholder')}
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    disabled={chatLoading || isGuestLimitReached}
                    maxLength={5000}
                  />
                  <button
                    type="submit"
                    className="chat-send-btn"
                    disabled={!chatInput.trim() || chatLoading || isGuestLimitReached}
                  >
                    <i className="fas fa-paper-plane"></i>
                    <span>{t('bhaktiSamadhan.chat.askButton')}</span>
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* === KUNDLI TAB === */}
          {activeTab === 'kundli' && (
            <div className="kundli-section">
              <div className="kundli-card">
                <h2 className="kundli-card-title">{t('bhaktiSamadhan.kundli.title', {}, 'Kundli (Generate Chart)')}</h2>
                <p className="kundli-card-desc">
                  {t('bhaktiSamadhan.kundli.desc', {}, 'Enter birth details to generate and analyze your kundli using Vedic astrology APIs.')}
                </p>

                <form className="kundli-form" onSubmit={handleAnalyzeKundli}>
                  <div className="kundli-grid">
                    <div className="kundli-field">
                      <label>{t('bhaktiSamadhan.kundli.name', {}, 'Name')}</label>
                      <input
                        type="text"
                        value={kundliForm.name}
                        onChange={(e) => handleKundliInputChange('name', e.target.value)}
                        placeholder={t('bhaktiSamadhan.kundli.namePlaceholder', {}, 'Your full name')}
                        disabled={kundliLoading}
                      />
                    </div>
                    <div className="kundli-field">
                      <label>{t('bhaktiSamadhan.kundli.birthPlace', {}, 'Birth Place')}</label>
                      <input
                        type="text"
                        value={kundliForm.birthPlace}
                        onChange={(e) => handleKundliInputChange('birthPlace', e.target.value)}
                        placeholder={t('bhaktiSamadhan.kundli.birthPlacePlaceholder', {}, 'City, State, Country')}
                        disabled={kundliLoading}
                      />
                    </div>
                    <div className="kundli-field">
                      <label>{t('bhaktiSamadhan.kundli.dateOfBirth', {}, 'Date of Birth')}</label>
                      <input
                        type="date"
                        value={kundliForm.dateOfBirth}
                        onChange={(e) => handleKundliInputChange('dateOfBirth', e.target.value)}
                        disabled={kundliLoading}
                        required
                      />
                    </div>
                    <div className="kundli-field">
                      <label>{t('bhaktiSamadhan.kundli.timeOfBirth', {}, 'Time of Birth')}</label>
                      <input
                        type="time"
                        value={kundliForm.timeOfBirth}
                        onChange={(e) => handleKundliInputChange('timeOfBirth', e.target.value)}
                        disabled={kundliLoading}
                        required
                      />
                    </div>
                    <div className="kundli-field">
                      <label>{t('bhaktiSamadhan.kundli.latitude', {}, 'Latitude')}</label>
                      <input
                        type="number"
                        step="any"
                        value={kundliForm.latitude}
                        onChange={(e) => handleKundliInputChange('latitude', e.target.value)}
                        placeholder="19.0760"
                        disabled={kundliLoading}
                        required
                      />
                    </div>
                    <div className="kundli-field">
                      <label>{t('bhaktiSamadhan.kundli.longitude', {}, 'Longitude')}</label>
                      <input
                        type="number"
                        step="any"
                        value={kundliForm.longitude}
                        onChange={(e) => handleKundliInputChange('longitude', e.target.value)}
                        placeholder="72.8777"
                        disabled={kundliLoading}
                        required
                      />
                    </div>
                    <div className="kundli-field">
                      <label>{t('bhaktiSamadhan.kundli.timezone', {}, 'Timezone (UTC Offset)')}</label>
                      <select
                        value={kundliForm.timezone}
                        onChange={(e) => handleKundliInputChange('timezone', e.target.value)}
                        disabled={kundliLoading}
                        required
                      >
                        {timezoneOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="kundli-submit-btn"
                    disabled={kundliLoading || isGuestLimitReached || astrologyStatusLoading || !astrologyStatus.ready}
                  >
                    {kundliLoading
                      ? t('bhaktiSamadhan.kundli.loading', {}, 'Analyzing Kundli...')
                      : astrologyStatusLoading
                        ? t('bhaktiSamadhan.kundli.checking', {}, 'Checking astrology service...')
                        : !astrologyStatus.ready
                          ? t('bhaktiSamadhan.kundli.unavailable', {}, 'Kundli Service Unavailable')
                      : t('bhaktiSamadhan.kundli.submit', {}, 'Generate & Analyze Kundli')}
                  </button>
                </form>

                {!astrologyStatus.ready && (
                  <div className="kundli-status-note">
                    {astrologyStatus.message || t('bhaktiSamadhan.kundli.unavailable', {}, 'Kundli service is currently unavailable.')}
                  </div>
                )}

                {isGuestLimitReached && (
                  <div className="guest-limit-cta">
                    <p>{guestLimitInfo.message}</p>
                    <div className="guest-limit-actions">
                      <Link to="/login" className="guest-limit-link">{t('nav.login')}</Link>
                      <Link to="/signup" className="guest-limit-link secondary">{t('nav.signUp')}</Link>
                    </div>
                  </div>
                )}

                {kundliError && <div className="kundli-error">{kundliError}</div>}

                {kundliResult?.success && (
                  <div className="kundli-result">
                    <h3>{t('bhaktiSamadhan.kundli.chartTitle', {}, 'Lagna / Ascendant / Basic Birth Chart')}</h3>
                    <div className="kundli-chart-wrap">
                      <div className="vedic-north-chart" role="img" aria-label={t('bhaktiSamadhan.kundli.chartTitle', {}, 'Lagna / Ascendant / Basic Birth Chart')}>
                        <svg className="vedic-chart-lines" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                          <rect x="2" y="2" width="96" height="96" />
                          <line x1="2" y1="2" x2="98" y2="98" />
                          <line x1="98" y1="2" x2="2" y2="98" />
                          <line x1="50" y1="2" x2="98" y2="50" />
                          <line x1="98" y1="50" x2="50" y2="98" />
                          <line x1="50" y1="98" x2="2" y2="50" />
                          <line x1="2" y1="50" x2="50" y2="2" />
                        </svg>

                        {NORTH_INDIAN_HOUSE_LAYOUT.map((cell) => (
                          <React.Fragment key={`house-${cell.house}`}>
                            <span
                              className="north-house-number"
                              style={{ top: cell.signTop, left: cell.signLeft }}
                            >
                              {kundliSignNumbers[cell.house - 1]}
                            </span>
                            {!!kundliHousePlanets[cell.house - 1] && (
                              <span
                                className="north-house-planets"
                                style={{ top: cell.planetTop, left: cell.planetLeft }}
                              >
                                {kundliHousePlanets[cell.house - 1]}
                              </span>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>

                    <h3>{t('bhaktiSamadhan.kundli.summaryTitle', {}, 'Summary')}</h3>
                    <div className="kundli-summary-grid">
                      <div><strong>{t('bhaktiSamadhan.kundli.ascendant', {}, 'Ascendant')}:</strong> {kundliResult.summary?.ascendant || '-'}</div>
                      <div><strong>{t('bhaktiSamadhan.kundli.moonSign', {}, 'Moon Sign')}:</strong> {kundliResult.summary?.moonSign || '-'}</div>
                      <div><strong>{t('bhaktiSamadhan.kundli.nakshatra', {}, 'Nakshatra')}:</strong> {kundliResult.summary?.nakshatra || '-'}</div>
                      <div><strong>{t('bhaktiSamadhan.kundli.tithi', {}, 'Tithi')}:</strong> {kundliResult.summary?.tithi || '-'}</div>
                      <div><strong>{t('bhaktiSamadhan.kundli.yog', {}, 'Yog')}:</strong> {kundliResult.summary?.yog || '-'}</div>
                    </div>

                    <h3>{t('bhaktiSamadhan.kundli.placementsTitle', {}, 'Key Planetary Placements')}</h3>
                    <div className="kundli-placement-grid">
                      {(kundliResult.keyPlacements || []).map((item, idx) => (
                        <div key={`${item.name || 'planet'}-${idx}`} className="kundli-placement-card">
                          <p><strong>{item.name || '-'}</strong></p>
                          <p>{t('bhaktiSamadhan.kundli.sign', {}, 'Sign')}: {item.sign || '-'}</p>
                          <p>{t('bhaktiSamadhan.kundli.house', {}, 'House')}: {item.house || '-'}</p>
                          <p>{t('bhaktiSamadhan.kundli.retrograde', {}, 'Retrograde')}: {item.retrograde ? t('common.yes', {}, 'Yes') : t('common.no', {}, 'No')}</p>
                        </div>
                      ))}
                    </div>

                    {kundliResult.remedyInsights && (
                      <div className="kundli-remedy-section">
                        <h3>{t('bhaktiSamadhan.kundli.remediesTitle', {}, 'Suggested Remedies')}</h3>
                        <p className="kundli-remedy-assessment">
                          {kundliResult.remedyInsights.assessment}
                        </p>

                        <div className="kundli-remedy-grid">
                          {(kundliResult.remedyInsights.suggestions || []).map((item, idx) => (
                            <div key={`${item.id || 'remedy'}-${idx}`} className="kundli-remedy-card">
                              <p className="kundli-remedy-title">{item.title || '-'}</p>
                              <p className="kundli-remedy-focus">{item.focus || '-'}</p>
                              <p className="kundli-remedy-intensity">
                                <strong>{t('bhaktiSamadhan.kundli.remedyIntensity', {}, 'Intensity')}:</strong> {item.intensity || '-'}
                              </p>
                              <ul className="kundli-remedy-list">
                                {(item.remedies || []).map((remedy, i) => (
                                  <li key={`${item.id || 'remedy'}-point-${i}`}>{remedy}</li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>

                        {kundliResult.remedyInsights.disclaimer && (
                          <p className="kundli-remedy-disclaimer">{kundliResult.remedyInsights.disclaimer}</p>
                        )}
                      </div>
                    )}

                    {Array.isArray(kundliResult.warnings) && kundliResult.warnings.length > 0 && (
                      <div className="kundli-warnings">
                        <h4>{t('bhaktiSamadhan.kundli.warningsTitle', {}, 'Warnings')}</h4>
                        {kundliResult.warnings.map((w, i) => (
                          <p key={`${w.endpoint || 'warning'}-${i}`}>• {w.endpoint}: {w.message}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* === BROWSE REMEDIES TAB === */}
          {activeTab === 'remedies' && (
            <div className="remedies-section">
              <h2 className="remedies-title">{t('bhaktiSamadhan.remedies.browseByCategory')}</h2>
              <div className="remedies-categories">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    className={`category-pill ${selectedCategory === cat ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {getCategoryLabel(cat)}
                  </button>
                ))}
              </div>
              <div className="remedies-grid">
                {REMEDIES
                  .filter((r) => selectedCategory === 'All Remedies' || r.category === selectedCategory)
                  .map((remedy, idx) => {
                    const localizedRemedy = getLocalizedRemedy(remedy);
                    const localizedCategory = getCategoryLabel(remedy.category);
                    return (
                      <div key={idx} className="remedy-card" onClick={() => {
                        setAdvisorInput(t('bhaktiSamadhan.remedies.morePrompt', {
                          title: localizedRemedy.title,
                          desc: localizedRemedy.desc
                        }, `Tell me more about ${localizedRemedy.title} — ${localizedRemedy.desc}`));
                        setActiveTab('advisor');
                      }}>
                        <div className="remedy-card-header">
                          <h3 className="remedy-name">{localizedRemedy.title}</h3>
                          <span className={`remedy-category-badge cat-${remedy.category.toLowerCase().replace(/\s+/g, '-')}`}>{localizedCategory}</span>
                        </div>
                        <p className="remedy-desc">{localizedRemedy.desc}</p>
                        <div className="remedy-deity-section">
                          <h4 className="remedy-deity-label">🙏 {t('bhaktiSamadhan.remedies.primaryDeity')}</h4>
                          <p className="remedy-deity-name">{localizedRemedy.deity}</p>
                          <p className="remedy-deity-desc">{localizedRemedy.deityDesc}</p>
                        </div>
                        <div className="remedy-poojas-section">
                          <h4 className="remedy-poojas-label">🙏 {t('bhaktiSamadhan.remedies.recommendedPoojas')}</h4>
                          <div className="remedy-poojas">
                            {localizedRemedy.poojas.map((p, i) => (
                              <span key={i} className="remedy-pooja-tag">{p}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BhaktiSamadhan;
